import type { JSX } from 'react';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { WireSourceInstance, WireSourceKind } from '@logistics/contracts';
import { api } from '../client';
import { fieldLabel, formError, input } from '../shell/look';
import { FormFooter } from './form-footer';
import { explainSave } from './save-errors';
import type { SaveFailure } from './save-errors';
import { SourceField } from './source-field';
import { firstSourceError, initialSourceValues, sourceFormSchema } from './source-schema';
import type { SourceFormValues } from './source-schema';

/**
 * Add or edit one instance (design L619–640). The fields and their schema come
 * from the server's descriptor, so this form is the same for every plugin. A
 * stored credential is never sent to the panel, so its box shows empty, and an
 * empty secret on save means "keep it".
 */
export function SourceForm({
  descriptor,
  namePlaceholder,
  existing,
  onSaved,
  back,
}: {
  descriptor: WireSourceKind;
  namePlaceholder: string;
  existing: WireSourceInstance | undefined;
  onSaved: () => void;
  back: { label: string; run: () => void };
}): JSX.Element {
  const [schema] = useState(() => sourceFormSchema(descriptor, existing));
  const form = useForm<SourceFormValues>({
    resolver: zodResolver(schema),
    defaultValues: initialSourceValues(descriptor, existing),
  });
  const [failure, setFailure] = useState<SaveFailure | undefined>(undefined);

  const submit = form.handleSubmit(async ({ label, fields }) => {
    setFailure(undefined);
    const body = { ...(label === '' ? {} : { label }), fields };
    const result =
      existing === undefined
        ? await api.instances.add({ body: { ...body, kind: descriptor.kind } })
        : await api.instances.update({ params: { id: existing.id }, body });
    if (result.status === 200 || result.status === 201) onSaved();
    else setFailure(explainSave(result.status, result.body));
  });

  const invalid = firstSourceError(descriptor, form.formState.errors);
  const busy = form.formState.isSubmitting;

  return (
    <form noValidate className="flex flex-1 flex-col gap-3.5" onSubmit={(e) => void submit(e)}>
      <label htmlFor="instance-label" className={fieldLabel}>
        Display name
        <input
          id="instance-label"
          {...form.register('label')}
          placeholder={namePlaceholder}
          className={input}
        />
      </label>
      {descriptor.fields.map((f) => (
        <Controller
          key={f.name}
          control={form.control}
          name={`fields.${f.name}`}
          render={({ field }) => (
            <SourceField
              field={f}
              value={field.value}
              configured={existing?.secretsPresent.includes(f.name) ?? false}
              onChange={field.onChange}
            />
          )}
        />
      ))}
      {invalid !== undefined && (
        <div role="alert" className={formError}>
          {invalid}
        </div>
      )}
      {invalid === undefined && failure !== undefined && <Failure failure={failure} />}
      <FormFooter
        back={back}
        busy={busy}
        label={busy ? 'Verifying…' : existing === undefined ? 'Connect & verify' : 'Save & verify'}
      />
    </form>
  );
}

/** What the API refused, and why, one reason per line. */
function Failure({ failure }: { failure: SaveFailure }): JSX.Element {
  return (
    <div role="alert" className={formError}>
      <div>{failure.title}</div>
      {failure.items.length > 0 && (
        <ul className="m-0 mt-1 list-disc pl-4 [overflow-wrap:anywhere]">
          {failure.items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
